class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (this.dataset.productId !== productId) return;

        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#syncInitialCardImages();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Align each new card gallery with its checked swatch before pointer interaction.
   */
  #syncInitialCardImages() {
    requestAnimationFrame(() => {
      const cards = this.querySelectorAll('.resource-list__item');

      for (const card of cards) {
        const selectedSwatch = card.querySelector('input[type="radio"]:checked[data-option-media-id]');
        const selectedMediaId = selectedSwatch?.getAttribute('data-option-media-id');
        const slides = card.querySelectorAll('slideshow-slide');
        const selectedSlide = selectedMediaId
          ? [...slides].find((slide) => slide.getAttribute('slide-id') === selectedMediaId)
          : card.querySelector('slideshow-slide:not([hidden])');
        const image = selectedSlide?.querySelector('img');
        if (!(selectedSlide instanceof HTMLElement) || !(image instanceof HTMLImageElement)) continue;

        image.loading = 'eager';

        const slideshow = /** @type {any} */ (card.querySelector('slideshow-component'));
        if (!selectedMediaId || !slideshow) continue;

        if (slideshow.disabled || slideshow.hasAttribute('disabled')) {
          for (const slide of slides) {
            slide.toggleAttribute('reveal', slide === selectedSlide && slide.hasAttribute('hidden'));
            slide.setAttribute('aria-hidden', String(slide !== selectedSlide));
          }

          const scroller = slideshow.querySelector('[ref="scroller"]');
          requestAnimationFrame(() => scroller?.scrollTo({ left: selectedSlide.offsetLeft, behavior: 'instant' }));
          continue;
        }

        if (typeof slideshow.select !== 'function') continue;
        requestAnimationFrame(() => slideshow.select({ id: selectedMediaId }, undefined, { animate: false }));
      }
    });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
      }
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
